export type ProjectStatus = 'IDEA' | 'RESEARCHING' | 'OUTLINING' | 'DRAFTING' | 'EDITING' | 'COMPLETE';
export type ProjectMode = 'AUTOPILOT' | 'COPILOT' | 'MANUAL';

export type CitationStyle = 
  | 'APA'           // American Psychological Association (7th ed)
  | 'MLA'           // Modern Language Association (9th ed)
  | 'Chicago-Notes' // Chicago Manual of Style - Notes & Bibliography
  | 'Chicago-Author'// Chicago Manual of Style - Author-Date
  | 'Harvard'       // Harvard Referencing
  | 'IEEE'          // IEEE Citation Style
  | 'Turabian'      // Turabian Style
  | 'Vancouver'     // Vancouver/ICMJE Style (medical)
  | 'AMA'           // American Medical Association
  | 'none';

export type ManuscriptType = 
  | 'how-to'
  | 'business'
  | 'self-help'
  | 'memoir'
  | 'biography'
  | 'academic'
  | 'research-paper'
  | 'thesis'
  | 'scientific-paper'
  | 'medical-paper'
  | 'legal'
  | 'journalism'
  | 'thought-leadership'
  | 'textbook'
  | 'essay-collection'
  | 'narrative-nonfiction'
  | 'ebook'
  | 'other';

export interface ManuscriptTypeInfo {
  label: string;
  description: string;
  defaultCitation: CitationStyle;
  suggestedWordCount: number;
  icon?: string;
}

export const MANUSCRIPT_TYPES: Record<ManuscriptType, ManuscriptTypeInfo> = {
  'how-to': {
    label: 'How-To Guide',
    description: 'Practical guides typically don\'t require formal citations',
    defaultCitation: 'none',
    suggestedWordCount: 35000,
  },
  'business': {
    label: 'Business & Leadership',
    description: 'Business books commonly use APA style',
    defaultCitation: 'APA',
    suggestedWordCount: 50000,
  },
  'self-help': {
    label: 'Self-Improvement',
    description: 'Self-help books rarely require formal citations',
    defaultCitation: 'none',
    suggestedWordCount: 45000,
  },
  'memoir': {
    label: 'Memoir',
    description: 'Memoirs traditionally use Chicago style notes',
    defaultCitation: 'Chicago-Notes',
    suggestedWordCount: 70000,
  },
  'biography': {
    label: 'Biography',
    description: 'Biographies use Chicago notes for historical accuracy',
    defaultCitation: 'Chicago-Notes',
    suggestedWordCount: 80000,
  },
  'academic': {
    label: 'Academic & Scholarly',
    description: 'Academic works require rigorous APA citation',
    defaultCitation: 'APA',
    suggestedWordCount: 60000,
  },
  'research-paper': {
    label: 'Research Paper',
    description: 'MLA is standard for humanities papers',
    defaultCitation: 'MLA',
    suggestedWordCount: 10000,
  },
  'thesis': {
    label: 'Thesis / Dissertation',
    description: 'Academic theses typically use APA style',
    defaultCitation: 'APA',
    suggestedWordCount: 80000,
  },
  'scientific-paper': {
    label: 'Scientific Paper',
    description: 'Sciences typically use APA style',
    defaultCitation: 'APA',
    suggestedWordCount: 8000,
  },
  'medical-paper': {
    label: 'Medical / Healthcare',
    description: 'Medical journals use Vancouver/ICMJE style',
    defaultCitation: 'Vancouver',
    suggestedWordCount: 6000,
  },
  'legal': {
    label: 'Legal Writing',
    description: 'Legal writing uses footnote citations',
    defaultCitation: 'Chicago-Notes',
    suggestedWordCount: 40000,
  },
  'journalism': {
    label: 'Journalism / Article',
    description: 'Journalistic writing typically informal',
    defaultCitation: 'none',
    suggestedWordCount: 5000,
  },
  'thought-leadership': {
    label: 'Thought Leadership',
    description: 'Usually informal with optional citations',
    defaultCitation: 'none',
    suggestedWordCount: 40000,
  },
  'textbook': {
    label: 'Textbook / Educational',
    description: 'Educational materials commonly use APA',
    defaultCitation: 'APA',
    suggestedWordCount: 80000,
  },
  'essay-collection': {
    label: 'Essay Collection',
    description: 'Essay collections often use MLA',
    defaultCitation: 'MLA',
    suggestedWordCount: 40000,
  },
  'narrative-nonfiction': {
    label: 'Narrative Nonfiction',
    description: 'Creative nonfiction uses Chicago notes',
    defaultCitation: 'Chicago-Notes',
    suggestedWordCount: 70000,
  },
  'ebook': {
    label: 'Ebook / Lead Magnet',
    description: 'Short, focused ebooks for lead generation and marketing',
    defaultCitation: 'none',
    suggestedWordCount: 5000,
  },
  'other': {
    label: 'Other',
    description: 'Custom manuscript type',
    defaultCitation: 'none',
    suggestedWordCount: 50000,
  },
};

export interface ProjectSettings {
  tone: string;
  targetAudienceDescription: string;
  readingLevel: 'General' | 'High School' | 'College' | 'Expert';
  voice: 'first-person' | 'third-person';
  tense: 'present' | 'past';
  formality: 'casual' | 'neutral' | 'formal';
  citationStyle: CitationStyle;
  language: string;
  chapterStructure: 'short' | 'medium' | 'long';
  modelProvider?: string;
  modelName?: string;
  targetWordCount?: number;
  manuscriptType?: ManuscriptType;
}

export interface Project {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  status: ProjectStatus;
  mode: ProjectMode;
  settings: ProjectSettings;
  wordCount: number;
  chapterCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Chapter {
  id: string;
  projectId: string;
  title: string;
  orderIndex: number;
  summary?: string;
  status: 'PLANNED' | 'OUTLINED' | 'DRAFTING' | 'DRAFTED' | 'EDITING' | 'DONE';
  createdAt: string;
  updatedAt: string;
}

export interface Section {
  id: string;
  chapterId: string;
  title: string;
  orderIndex: number;
  content: string;
  status: 'PLANNED' | 'DRAFTED' | 'EDITING' | 'DONE';
  createdAt: string;
  updatedAt: string;
}

export interface ResearchItem {
  id: string;
  projectId: string;
  type: 'UPLOAD' | 'WEB_SNAPSHOT' | 'NOTE' | 'IMPORTED_BOOKMARK' | 'QUOTE';
  title: string;
  sourceUrl?: string;
  rawText: string;
  metadata?: Record<string, unknown>;
  filePath?: string;
  createdAt: string;
  updatedAt: string;
}

export const defaultProjectSettings: ProjectSettings = {
  tone: 'conversational',
  targetAudienceDescription: '',
  readingLevel: 'General',
  voice: 'first-person',
  tense: 'present',
  formality: 'neutral',
  citationStyle: 'none',
  language: 'English',
  chapterStructure: 'medium',
};

export const STATUS_LABELS: Record<ProjectStatus, string> = {
  IDEA: 'Idea',
  RESEARCHING: 'Researching',
  OUTLINING: 'Outlining',
  DRAFTING: 'Drafting',
  EDITING: 'Editing',
  COMPLETE: 'Complete',
};

export const MODE_LABELS: Record<ProjectMode, string> = {
  AUTOPILOT: 'AutoPilot',
  COPILOT: 'CoPilot',
  MANUAL: 'Manual',
};

export const MODE_DESCRIPTIONS: Record<ProjectMode, string> = {
  AUTOPILOT: 'AI drives the entire process with minimal input',
  COPILOT: 'AI assists at each step, you approve before proceeding',
  MANUAL: 'You control everything, AI is your assistant',
};
